import React from 'react';
import { useAdMetricsByDimension, GetAdMetricsByDimensionResponseRecord } from '../hooks/useAdMetricsByDimension';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { PaidSearchTopicCardDef, paidSearchMetrics } from './constants';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import ReactDOMServer from 'react-dom/server';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import {
  useMainTimePeriodShortDisplayName,
  useComparisonTimePeriodShortDisplayName,
  useAppSelector
} from 'src/utils/Hooks';
import { useCompanyName } from 'src/utils/Hooks/reduxSelectors';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const BARS_IN_CHART = 100;

const titleOverrides = {
  conversionRate: 'Conversion Rate'
};

const metricSortOrder = [
  'adClicks',
  'adImpressions',
  'ctr',
  'cpc',
  'cpm',
  'cpa',
  'adSales',
  'conversionRate',
  'adUnitsSold',
  'roas'
];

const metricTypeToMetricInfo = paidSearchMetrics.reduce((acc, metricInfo) => {
  acc.set(metricInfo.fieldId, metricInfo);
  return acc;
}, new Map<string, PaidSearchTopicCardDef>());

const entityDropdownOptions = [
  { id: 'searchKeyword', label: 'Keyword' },
  { id: 'stacklineSku', label: 'Product' },
  { id: 'subcategoryId', label: 'Subcategory' },
  { id: 'categoryId', label: 'Category' },
  { id: 'brandId', label: 'Brand' },
  { id: 'campaignId', label: 'Campaign' },
  { id: 'entityId', label: 'Entity' }
];
/**
 * Double dropdown bar chart for paid search topics
 */
export const PaidSearchTopicsDoubleOptionBarChart = () => {
  const [selectedMetric, setSelectedMetric] = React.useState('adSpend');
  const [selectedGroupBy, setSelectedGroupBy] = React.useState('searchKeyword');

  const { displayName: mainPeriodDisplay } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplay } = useAppSelector((state) => state.comparisonTimePeriod);
  /** Dimension refers to the group by field in the way we use it throughout the rest of the site,
   * this particular endpoint just uses the term "dimension"
   */
  const { response, isLoading } = useAdMetricsByDimension(selectedMetric, selectedGroupBy);
  const categories = response[0].records.map((record) => ({
    id: record.dimension,
    name: record.name,
    type:
      selectedGroupBy === 'stacklineSku'
        ? 'product'
        : ['campaignId', 'entityId', 'searchKeyword'].includes(selectedGroupBy)
        ? ''
        : selectedGroupBy.replace('Id', ''),
    stacklineSku: selectedGroupBy === 'stacklineSku' ? record.fullRecord.stacklineSku : undefined,
    retailerSku: selectedGroupBy === 'stacklineSku' ? record.fullRecord.retailerSku : undefined
  }));

  const dimensionToMetricComparisonMap = response[1].records.reduce((acc, metricInfo) => {
    acc.set(metricInfo.dimension, metricInfo);
    return acc;
  }, new Map<string, GetAdMetricsByDimensionResponseRecord>());

  const primaryData: [string, number][] = response[0].records.map((record) => [
    record.name,
    record[selectedMetric] ?? 0
  ]);

  const comparisonData: [string, number][] = categories.map((category) => [
    category.name,
    dimensionToMetricComparisonMap.get(category.id)?.[selectedMetric] ?? 0
  ]);

  const metricType = metricTypeToMetricInfo.get(selectedMetric)?.metricType;

  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const companyName = useCompanyName();

  const taxonomyText = useTaxonomySubtitleText();
  const getSubtitle = () => {
    const metric = paidSearchMetrics.find((m) => m.fieldId === selectedMetric)?.title ?? '';
    const metricName = ['ROAS', 'CPA', 'CPM', 'CPC', 'CTR %'].includes(metric) ? metric : metric.toLowerCase();
    const groupByName = entityDropdownOptions.find((option) => option.id === selectedGroupBy)?.label ?? '';

    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the ${metricName} for ${companyName}`}
          {taxonomyText}
          {` in ${entityPhrase} in each ${groupByName.toLowerCase()}`}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconChartWithLegend
      primaryLegendProps={{ displayName: mainPeriodDisplay }}
      comparisonLegendProps={{ displayName: comparisonPeriodDisplay }}
      subtitle={getSubtitle()}
      includeSummaryInfo={false}
      loading={isLoading}
      title={
        <Flex alignItems="center" gap={6}>
          <SlDropdownMenu
            buttonVariant="title"
            options={paidSearchMetrics
              .filter((metric) => metric.fieldId !== 'keywordCount')
              .map((metric) => ({
                id: metric.fieldId,
                label: titleOverrides[metric.fieldId] || metric.title
              }))
              .sort((a, b) => metricSortOrder.indexOf(a.id) - metricSortOrder.indexOf(b.id))}
            selectedId={selectedMetric}
            onChange={(option) => setSelectedMetric(option.id)}
          />
          <Text variant="h4">by</Text>
          <SlDropdownMenu
            buttonVariant="title"
            options={entityDropdownOptions}
            selectedId={selectedGroupBy}
            onChange={(option) => setSelectedGroupBy(option.id)}
          />
        </Flex>
      }
      convertSeriesToDelimitedData={async () => {
        const metricName = titleOverrides[selectedMetric] || metricTypeToMetricInfo.get(selectedMetric)?.title;
        return saveBarChartAsDelimitedData(primaryData, comparisonData, {
          fileTitle: `Paid Search - ${metricName} by ${
            entityDropdownOptions.find((option) => option.id === selectedGroupBy)?.label
          }`,
          metricLabel: metricName
        });
      }}
    >
      <TopEntityChartV2
        xAxisFormatter={
          ['brandId', 'subcategoryId', 'categoryId', 'stacklineSku'].includes(selectedGroupBy)
            ? undefined
            : (xAxisCategory) => {
                return ReactDOMServer.renderToStaticMarkup(
                  <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
                );
              }
        }
        chartSeries={[
          {
            type: 'column',
            data: comparisonData.slice(0, BARS_IN_CHART),
            categories,
            metricType
          },
          {
            type: 'column',
            data: primaryData.slice(0, BARS_IN_CHART),
            categories,
            metricType
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};
